/**
 * 小程序 web-view 内分享参数上报工具
 *
 * 说明：
 * 微信小程序的 web-view 中无法直接调用微信 JS-SDK 的分享接口（updateAppMessageShareData 等）。
 * 需要在 H5 侧通过 wx.miniProgram.postMessage 把分享参数上报给小程序宿主，
 * 由小程序在 onShareAppMessage / onShareTimeline 中返回分享卡片。
 *
 * 使用方式：
 *   import { setMiniProgramShare, isMiniProgram } from '@/utils/miniProgramShare'
 *   setMiniProgramShare({ title, desc, link, imgUrl })
 */

// 默认分享兜底
const DEFAULT_SHARE = {
  title: "陕西省保健学会",
  desc: "坚持“服务政府、服务大众、服务社会”的宗旨，为陕西省健康事业提供全方位服务。",
  imgUrl: "https://media.lotusdata.com/26288543483559960.png",
};

// 缓存最后一次调用参数，方便页面切换/重试时读取
let lastShareConfig = { ...DEFAULT_SHARE };

/**
 * 判断是否运行在微信小程序的 web-view 环境中
 * 注意：首次进入页面时可能检测不到 wx.miniProgram，需要等待 wx.ready 后再调用
 */
export function isMiniProgram() {
  return new Promise((resolve) => {
    // 部分浏览器下 window.__wxjs_environment 可以直接判断
    if (window.__wxjs_environment === "miniprogram") {
      resolve(true);
      return;
    }

    // 如果 wx 对象不存在，说明不是微信环境
    if (typeof window.wx === "undefined" || !window.wx.miniProgram) {
      resolve(false);
      return;
    }

    // 通过 wx.miniProgram.getEnv 判断
    try {
      window.wx.miniProgram.getEnv((res) => {
        resolve(res.miniprogram === true);
      });
    } catch (e) {
      resolve(false);
    }
  });
}

/**
 * 向小程序宿主上报分享参数
 * @param {Object} options
 * @param {String} options.title   分享标题
 * @param {String} options.desc    分享描述（朋友）
 * @param {String} options.link    分享链接（H5 完整链接，小程序侧可拼接回 web-view）
 * @param {String} options.imgUrl  分享图标
 */
export async function setMiniProgramShare(options = {}) {
  const config = {
    ...DEFAULT_SHARE,
    ...lastShareConfig,
    ...options,
  };

  // 没有传 link 时使用当前页面地址（去掉 hash 后的 # 号可能让小程序解析异常，建议保留完整 href）
  if (!config.link) {
    config.link = window.location.href;
  }

  lastShareConfig = config;

  // 只有小程序环境才需要 postMessage
  const inMiniProgram = await isMiniProgram();
  if (!inMiniProgram) {
    return;
  }

  // 尝试 postMessage，失败后等待 wx.ready 再重试一次
  tryPostMessage(config);
}

/**
 * 获取当前缓存的分享参数（可在小程序侧或页面切换时使用）
 */
export function getMiniProgramShare() {
  return { ...lastShareConfig };
}

/**
 * 尝试执行 wx.miniProgram.postMessage
 */
function tryPostMessage(config) {
  if (
    !window.wx ||
    !window.wx.miniProgram ||
    !window.wx.miniProgram.postMessage
  ) {
    // wx 未就绪，等待 wx.ready 后重试
    if (window.wx && window.wx.ready) {
      window.wx.ready(() => {
        doPostMessage(config);
      });
    }
    return;
  }
  doPostMessage(config);
}

function doPostMessage(config) {
  try {
    window.wx.miniProgram.postMessage({
      data: {
        type: "share",
        title: config.title,
        desc: config.desc,
        link: config.link,
        imgUrl: config.imgUrl,
      },
    });
    // 同时发送一份新的消息，确保小程序 bindmessage 能收到
    if (
      window.wx.miniProgram.postMessage &&
      typeof window.wx.miniProgram.postMessage === "function"
    ) {
      console.log("[miniProgramShare] postMessage 已发送", config);
    }
  } catch (e) {
    console.error("[miniProgramShare] postMessage 发送失败", e);
  }
}

/**
 * 统一设置分享（兼容普通微信浏览器 + 小程序 web-view）
 * 普通微信环境：使用已有的 Vue.prototype.$wxShare
 * 小程序 web-view：使用 postMessage 上报
 */
export function setUniversalShare(vm, options = {}) {
  if (!options.link) {
    options.link = window.location.href;
  }

  // 1. 如果是小程序环境，优先使用 postMessage 上报
  isMiniProgram().then((inMiniProgram) => {
    if (inMiniProgram) {
      setMiniProgramShare(options);
    }
  });

  // 2. 普通微信浏览器环境，使用 JS-SDK 分享
  //    注意：在 web-view 中 $wxShare 会失效，但普通微信内仍可正常工作
  if (vm && vm.$wxShare) {
    vm.$wxShare(options);
  }
}

export default {
  isMiniProgram,
  setMiniProgramShare,
  getMiniProgramShare,
  setUniversalShare,
};



// WEBPACK FOOTER //
// ./src/utils/miniProgramShare.js